/* Price */
/* Displays a price. This includes sale and range */
import { StyledScreenReaderSpan, buildResponsiveSize, formatPrice } from '@tvg/fuse-utils';
import PropTypes from 'prop-types';
import React from 'react';
import { priceConfig } from './config';
import {
	StyledBasicPrice,
	StyledPrevPrice,
	StyledPriceContainer,
	StyledSalePriceWrapper,
	StyledSavePrice,
} from './styled-price';

const { forwardRef } = React;

const propTypes = {
	price: PropTypes.number.isRequired,
	dataTestId: PropTypes.string,
	headingTag: PropTypes.string,
	isEuro: PropTypes.bool,
	prevPrice: PropTypes.number,
	saving: PropTypes.number,
	showCurrentFrom: PropTypes.bool,
	showPreviousFrom: PropTypes.bool,
	showSave: PropTypes.bool,
	size: PropTypes.oneOfType([
		PropTypes.oneOf(['xxs', 'xs', 's', 'm', 'l']),
		PropTypes.shape({
			xxs: PropTypes.string,
			xs: PropTypes.string,
			s: PropTypes.string,
			m: PropTypes.string,
			l: PropTypes.string,
			xl: PropTypes.string,
			xxl: PropTypes.string,
		}),
	]),
};

const defaultProps = {
	dataTestId: null,
	headingTag: 'h3',
	isEuro: false,
	prevPrice: 0,
	saving: 0,
	showCurrentFrom: false,
	showPreviousFrom: false,
	showSave: true,
	size: 'l',
};

const {
	basicPrice: basicPriceConfig,
	prevPrice: prevPriceConfig,
	savePrice: savePriceConfig,
} = priceConfig;

const Price = forwardRef(
	(
		{
			price,
			dataTestId,
			headingTag,
			isEuro,
			prevPrice,
			saving,
			showCurrentFrom,
			showPreviousFrom,
			showSave,
			size,
			...others
		},
		ref,
	) => {
		const formattedPrice = formatPrice(price);
		const formattedPrevPrice = formatPrice(prevPrice);
		const formattedSavePrice = formatPrice(saving);

		const [isResponsive] = buildResponsiveSize(size);
		const getResponsiveFontSize = (config) => {
			if (isResponsive) {
				const processedSize = {};
				Object.keys(size).forEach((viewport) => {
					processedSize[viewport] = config.font.size[size[viewport]];
				});

				return processedSize;
			}

			return config.font.size[size];
		};

		const getPriceText = (priceNum, showFrom, isSave = false) => {
			const saveText = showFrom ? 'Save up to ' : 'Save ';
			const fromText = showFrom ? 'From ' : '';
			const currency = isEuro ? '€' : '£';

			const prefix = isSave ? saveText : fromText;

			return `${prefix}${currency}${priceNum}`;
		};

		return (
			<StyledPriceContainer data-testid={dataTestId} ref={ref} {...others}>
				{!!price && !prevPrice && (
					<StyledBasicPrice
						tag={headingTag}
						size={getResponsiveFontSize(basicPriceConfig)}
						type={basicPriceConfig.font.type}
						data-testid={dataTestId && `${dataTestId}__basic`}
					>
						{getPriceText(formattedPrice, showCurrentFrom)}
					</StyledBasicPrice>
				)}
				{!!price && !!prevPrice && (
					<StyledSalePriceWrapper size={size}>
						<StyledBasicPrice
							prevPrice={formattedPrevPrice}
							size={getResponsiveFontSize(basicPriceConfig)}
							type={basicPriceConfig.font.type}
							tag={headingTag}
							data-testid={dataTestId && `${dataTestId}__basic`}
						>
							<StyledScreenReaderSpan>Current sale price is </StyledScreenReaderSpan>
							{getPriceText(formattedPrice, showCurrentFrom)}
						</StyledBasicPrice>
						{showSave && (
							<StyledSavePrice
								size={getResponsiveFontSize(savePriceConfig)}
								type={savePriceConfig.font.type}
								data-testid={dataTestId && `${dataTestId}__save`}
							>
								({getPriceText(formattedSavePrice, showCurrentFrom, true)})
							</StyledSavePrice>
						)}
					</StyledSalePriceWrapper>
				)}
				{!!prevPrice && (
					<StyledPrevPrice
						size={getResponsiveFontSize(prevPriceConfig)}
						type={prevPriceConfig.font.type}
						data-testid={dataTestId && `${dataTestId}__prev`}
					>
						<StyledScreenReaderSpan>Regular price was </StyledScreenReaderSpan>
						{getPriceText(formattedPrevPrice, showPreviousFrom)}
					</StyledPrevPrice>
				)}
			</StyledPriceContainer>
		);
	},
);

Price.propTypes = propTypes;
Price.defaultProps = defaultProps;

export default Price;
